import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography, { COLORS } from 'Components/UI/Typography';
import Row from './Row';

const styles = theme => ({
  root: {},
  title: {
    marginBottom: theme.spacing(2),
  },
});

const findPercentWidth = (width, items, labelRefs, itemIdx) => {
  const isLastitem = itemIdx === items.length - 1;
  const checkItem = items[itemIdx];
  const checkItemLabelWidth = labelRefs.current[checkItem.name].clientWidth;

  const remainingWidth = width - checkItemLabelWidth;
  const percentWidth = remainingWidth / (Math.abs(checkItem.percentOfTotal) * 100);

  for (let i = 0; i < items.length; i++) {
    if (i === itemIdx) {
      continue;
    }

    const currItem = items[i];
    const currItemLabelWidth = labelRefs.current[currItem.name].clientWidth;
    const currItemRemainingWidth = width - currItemLabelWidth;
    const currItemBarWidth = percentWidth * (currItem.percentOfTotal * 100);

    if (currItemRemainingWidth < currItemBarWidth && !isLastitem) {
      return findPercentWidth(width, items, labelRefs, itemIdx + 1);
    }
  }
  return percentWidth;
};

const getRowData = (width, items, labelRefs) => {
  const itemNames = items.map(v => v.name);
  const labelRefMap = labelRefs.current || {};

  // The chart is initialized when all items are present in the label ref map
  const chartInitialized = itemNames.length > 0 && itemNames.reduce((acc, curr) => {
    return acc && !!labelRefMap[curr];
  }, true);

  if (!chartInitialized) {
    return items.map(item => ({
      ...item,
      barWidth: 0,
      barScale: 0,
    }));
  }

  const percentWidth = findPercentWidth(width, items, labelRefs, 0);

  return items.map((item) => {
    const barWidth = item.sum < 0 ? 0 : percentWidth * (item.percentOfTotal * 100);
    return {
      ...item,
      barWidth,
      barScale: 1,
    };
  });
};

const BarChart = ({
  className,
  classes,
  title,
  items,
  color,
  width,
  style,
}) => {
  const labelRefs = useRef();

  // eslint-disable-next-line no-unused-vars
  const [_, setLabelsMounted] = useState(0);

  useEffect(() => {
    setLabelsMounted(prevState => prevState + 1);
  }, [labelRefs.current]);

  return (
    <div
      style={{ width, ...style }}
      className={classNames(classes.root, className)}
    >
      {title && (
        <Typography
          className={classes.title}
          variant="h3"
          color={color}
        >
          {title}
        </Typography>)
      }
      {getRowData(width, items, labelRefs).map((item, i) => {
        return (
          <Row
            key={`${item.name}-${i}`} // eslint-disable-line react/no-array-index-key
            labelRefs={labelRefs}
            itemName={item.name}
            BarProps={{
              transform: `scaleX(${item.barScale}) translateX(0px)`,
              width: item.barWidth,
              color,
            }}
            LabelProps={{
              label: item.barName,
              transform: `translateX(${item.barScale * item.barWidth}px)`,
            }}
          />
        );
      })}
    </div>
  );
};

BarChart.defaultProps = {
  className: '',
  title: null,
  style: {},
};

BarChart.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  title: PropTypes.string,
  items: PropTypes.array.isRequired,
  color: PropTypes.oneOf(COLORS).isRequired,
  width: PropTypes.number.isRequired,
  style: PropTypes.object,
};

export default withStyles(styles)(BarChart);
